<template>
  <div class="gen-edit">
    <div class="page-container">
      <!-- TOPBAR -->
      <header class="topbar">
        <div class="left">
          <h1 class="title">Products to Validate</h1>
          <div class="pill">{{ products.length }} pending</div>
        </div>

        <div class="right">
          <button class="btn btn-ghost" @click="toggleViewMode">
            {{ viewMode === 'compact' ? 'Expand all' : 'Collapse all' }}
          </button>
        </div>

        <div
          style="
            display: flex;
            gap: 10px;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 12px;
          "
        >
          <span style="font-weight: 800; font-size: 12px; color: #475569"
            >Platform</span
          >
          <select
            class="input"
            style="max-width: 200px"
            v-model="platform"
            @change="setPlatform(platform)"
          >
            <option
              v-for="p in platformOptions"
              :key="p.value"
              :value="p.value"
            >
              {{ p.label }}
            </option>
          </select>
        </div>
      </header>

      <!-- LIST -->
      <section class="list-wrap">
        <div
          v-for="p in products"
          :key="p.id_backoffice"
          class="accordion-item"
        >
          <!-- HEADER -->
          <div class="accordion-header" @click="toggle(p.id_backoffice)">
            <div class="header-left">
              <span class="id">#{{ p.id_backoffice }}</span>
              <span class="qa-pill" :class="qaPillClass(p)" @click.stop>
                QA {{ qaScore(p) ?? '-' }}
              </span>

              <img
                :src="p.img || placeholderImg"
                class="thumb zoomable"
                @error="onImgError"
                @click.stop="openImage(p.img)"
              />

              <div class="summary">
                <strong class="name">{{ p.name }}</strong>
                <div class="meta">
                  <span>Brand: {{ p.brand || '-' }}</span>
                  <span>SKU: {{ first(p.sku) }}</span>
                  <span>EAN: {{ first(p.ean) }}</span>
                </div>
              </div>
            </div>

            <div class="header-actions">
              <button class="btn btn-ghost" @click.stop="edit(p)">Edit</button>

              <a
                v-if="p.id_idivia"
                class="btn btn-wp"
                :href="wpEditUrl(p)"
                target="_blank"
                @click.stop
              >
                WP
              </a>

              <button class="btn btn-primary" @click.stop="validate(p)">
                Validate
              </button>

              <span class="chevron">
                {{ open[p.id_backoffice] ? '▲' : '▼' }}
              </span>
            </div>
          </div>

          <!-- CONTENT -->
          <transition name="accordion">
            <div v-show="open[p.id_backoffice]" class="accordion-content">
              <!-- QA VALIDATOR (details) -->
              <div v-if="qaObj(p)" class="info-block qa-box">
                <div class="qa-top">
                  <h4 style="margin: 0">AI QA</h4>

                  <span class="qa-pill" :class="qaPillClass(p)">
                    QA {{ qaScore(p) ?? '-' }}
                    <span v-if="qaApproved(p) === true">✓</span>
                    <span v-else-if="qaApproved(p) === false">✕</span>
                  </span>
                </div>

                <p v-if="qaSummary(p)" class="qa-summary">{{ qaSummary(p) }}</p>

                <div class="qa-chips">
                  <span v-if="qaClaimRisk(p)" class="qa-chip">
                    Claim risk: <strong>{{ qaClaimRisk(p) }}</strong>
                  </span>
                  <span
                    v-if="qaCheck(p, 'structure_ok') !== null"
                    class="qa-chip"
                  >
                    Structure:
                    <strong>{{
                      qaCheck(p, 'structure_ok') ? 'OK' : 'Bad'
                    }}</strong>
                  </span>
                  <span v-if="qaCheck(p, 'html_ok') !== null" class="qa-chip">
                    HTML:
                    <strong>{{ qaCheck(p, 'html_ok') ? 'OK' : 'Bad' }}</strong>
                  </span>
                  <span v-if="qaCheck(p, 'seo_ok') !== null" class="qa-chip">
                    SEO:
                    <strong>{{ qaCheck(p, 'seo_ok') ? 'OK' : 'Bad' }}</strong>
                  </span>
                  <span
                    v-if="qaCheck(p, 'is_english') !== null"
                    class="qa-chip"
                  >
                    English:
                    <strong>{{
                      qaCheck(p, 'is_english') ? 'Yes' : 'No'
                    }}</strong>
                  </span>
                </div>

                <div v-if="qaIssues(p).length" class="qa-list">
                  <strong>Issues</strong>
                  <ul>
                    <li v-for="(it, idx) in qaIssues(p)" :key="'qi' + idx">
                      {{ it }}
                    </li>
                  </ul>
                </div>

                <div v-if="qaFixes(p).length" class="qa-list">
                  <strong>Suggested fixes</strong>
                  <ul>
                    <li v-for="(it, idx) in qaFixes(p)" :key="'qf' + idx">
                      {{ it }}
                    </li>
                  </ul>
                </div>
              </div>
              <!-- DESCRIPTIONS -->
              <div class="info-block">
                <h4>Description</h4>
                <div class="html" v-html="p.description_html || '-'" />
              </div>

              <div class="info-block">
                <h4>Short Description</h4>
                <div class="html" v-html="p.short_description_html || '-'" />
              </div>

              <!-- META -->
              <div class="info-grid">
                <div class="info-block">
                  <h4>Meta Title</h4>
                  <p>{{ p.meta_title || '-' }}</p>
                </div>

                <div class="info-block">
                  <h4>Meta Description</h4>
                  <p>{{ p.meta_description || '-' }}</p>
                </div>
              </div>

              <!-- TAGS -->
              <div class="info-block">
                <h4>Tags</h4>
                <div class="tags">
                  <span v-for="t in parseTags(p.tags)" :key="t" class="ai-chip">
                    {{ t }}
                  </span>
                </div>
              </div>

              <!-- LOGISTICS + STOCK -->
              <div class="info-grid">
                <div class="info-block">
                  <h4>Weight</h4>
                  <p>{{ p.weight ? p.weight + ' g' : '-' }}</p>
                </div>

                <div class="info-block">
                  <h4>HS Code</h4>
                  <p>{{ p.hscode || '-' }}</p>
                </div>

                <div class="info-block">
                  <h4>Country</h4>
                  <p>{{ p.country || '-' }}</p>
                </div>

                <div class="info-block">
                  <h4>AI Model</h4>
                  <p>{{ p.ai_type || '-' }}</p>
                </div>

                <div class="info-block">
                  <h4>Stock Status</h4>
                  <v-select
                    :options="status"
                    v-model="p.stock_status"
                    :clearable="false"
                    @input="updateStockStatus(p)"
                  />
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div v-if="!products.length" class="empty">🎉 Nothing to validate</div>
      </section>
    </div>
    <!-- IMAGE MODAL -->
    <transition name="fade">
      <div v-if="imageModal.open" class="image-modal" @click.self="closeImage">
        <div class="image-modal-content">
          <button class="close" @click="closeImage">✕</button>
          <img :src="imageModal.src" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  name: 'ValidateProducts',
  components: { vSelect },

  data() {
    return {
      API: this.$store.state.apiUrl,
      token: localStorage.getItem('token'),

      products: [],
      open: {},
      viewMode: 'compact',

      status: [
        'Infinity',
        'Normal',
        'Normalix',
        'Zero',
        'Discontinued',
        'MinC No',
        'MinC Low',
        'MinC Mid',
        'MinC High',
      ],

      placeholderImg:
        'https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg',

      imageModal: {
        open: false,
        src: null,
      },

      // Só o .com tem validação por agora (o /eu vem na fase 2; a Shopify saiu)
      platform: 'woocommerce',
      platformOptions: [{ value: 'woocommerce', label: 'WooCommerce (.com)' }],
    };
  },

  methods: {
    apiUrl(path) {
      const base = `${this.API}${path}`;
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}platform=${encodeURIComponent(this.platform)}`;
    },

    setPlatform(p) {
      this.platform = p;
      localStorage.setItem('gen_platform', p);
      this.load();
    },

    headers() {
      return { Authorization: `Bearer ${this.token}` };
    },

    async load() {
      const { data } = await axios.get(
        this.apiUrl('openai/products-to-validate'),
        { headers: this.headers() }
      );

      this.products = data.products || [];

      // default closed
      this.products.forEach((p) => {
        this.$set(this.open, p.id_backoffice, false);
      });
    },

    toggle(id) {
      this.$set(this.open, id, !this.open[id]);
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'compact' ? 'expanded' : 'compact';

      this.products.forEach((p) => {
        this.$set(this.open, p.id_backoffice, this.viewMode === 'expanded');
      });
    },

    first(v) {
      if (!v) return '-';
      try {
        return Array.isArray(v) ? v[0] : JSON.parse(v)[0];
      } catch {
        return v;
      }
    },

    parseTags(v) {
      try {
        return Array.isArray(v) ? v : JSON.parse(v);
      } catch {
        return [];
      }
    },

    qaObj(p) {
      const raw = p?.qa_validator_json;
      if (!raw) return null;

      try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        return null;
      }
    },

    qaScore(p) {
      const obj = this.qaObj(p);
      return Number.isFinite(obj?.score) ? obj.score : null;
    },

    qaPillClass(p) {
      const obj = this.qaObj(p);
      if (!obj || typeof obj.approved !== 'boolean') return 'qa-unk';
      return obj.approved ? 'qa-ok' : 'qa-bad';
    },

    qaApproved(p) {
      const obj = this.qaObj(p);
      return typeof obj?.approved === 'boolean' ? obj.approved : null;
    },

    qaSummary(p) {
      const obj = this.qaObj(p);
      return typeof obj?.summary === 'string' ? obj.summary : '';
    },

    qaClaimRisk(p) {
      const obj = this.qaObj(p);
      const risk = obj?.checks?.claim_risk;
      return ['low', 'medium', 'high'].includes(risk) ? risk : null;
    },

    qaCheck(p, key) {
      const obj = this.qaObj(p);
      const v = obj?.checks?.[key];
      return typeof v === 'boolean' ? v : null;
    },

    qaIssues(p) {
      const obj = this.qaObj(p);
      return Array.isArray(obj?.issues) ? obj.issues : [];
    },

    qaFixes(p) {
      const obj = this.qaObj(p);
      return Array.isArray(obj?.fixes) ? obj.fixes : [];
    },

    edit(p) {
      //Open in a new tab
      // this.$router.push({
      //   path: `/generated-products/${p.id_backoffice}`,
      //   query: { from: 'validate' },
      // });
      window.open(`/generated-products/${p.id_backoffice}`, '_blank');
    },

    wpEditUrl(p) {
      return `https://www.idivia.com/wp-admin/post.php?post=${p.id_idivia}&action=edit&lang=en`;
    },

    async validate(p) {
      const confirm = await Swal.fire({
        title: 'Validate product?',
        html: `<strong>${p.name}</strong>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Validate',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#16a34a',
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      await axios.put(
        `${this.API}openai/validate-product-info/${p.id_backoffice}`,
        {},
        { headers: this.headers() }
      );

      this.products = this.products.filter(
        (x) => x.id_backoffice !== p.id_backoffice
      );

      Swal.fire({
        icon: 'success',
        title: 'Validated',
        timer: 1200,
        showConfirmButton: false,
      });
    },

    async updateStockStatus(item) {
      try {
        await this.$store.dispatch('updateStockStatus', {
          id: item.id_backoffice,
          status: item.stock_status,
        });

        this.$swal({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Stock Status Atualizado',
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (err) {
        alert(err);
      }
    },

    openImage(src) {
      if (!src) return;
      this.imageModal.src = src;
      this.imageModal.open = true;
    },

    closeImage() {
      this.imageModal.open = false;
      this.imageModal.src = null;
    },

    onImgError(e) {
      e.target.src = this.placeholderImg;
    },
  },

  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.page-container {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 14px 28px;
}

/* TOPBAR */
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

/* LIST */
.list-wrap {
  background: transparent;
}

/* ITEM */
.accordion-item {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 12px;
  background: #fff;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f8fafc;
  cursor: pointer;
}

.header-left {
  display: flex;
  gap: 12px;
  align-items: center;
}

.id {
  background: #0f172a;
  color: #fff;
  font-size: 10px;
  padding: 4px 6px;
  border-radius: 6px;
}

.thumb {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.zoomable {
  cursor: zoom-in;
}

.summary .name {
  font-size: 15px;
  font-weight: 700;
}

.summary .meta span {
  font-size: 11px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 999px;
  margin-right: 6px;
}

.header-actions .btn {
  padding: 5px 10px;
  font-size: 12px;
  border-radius: 6px;
}

.btn-wp {
  background: #f1f5f9;
}

.accordion-content {
  padding: 14px;
  border-top: 1px dashed #e5e7eb;
}

.info-block {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.ai-chip {
  background: #eef2ff;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
}

.empty {
  padding: 40px;
  text-align: center;
  color: #64748b;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.25s ease;
}

.accordion-enter,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}

.image-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.image-modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.image-modal-content img {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 12px;
  background: #fff;
}

.image-modal-content .close {
  position: absolute;
  top: -12px;
  right: -12px;
  background: #0f172a;
  color: #fff;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  cursor: pointer;
}

/* fade animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.qa-pill {
  background: #f8fafc;
  color: #0f172a;
  font-size: 10px;
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-weight: 800;
}

.qa-ok {
  background: #ecfdf5;
  border-color: #bbf7d0;
}

.qa-bad {
  background: #fef2f2;
  border-color: #fecaca;
}

.qa-unk {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.qa-box {
  background: #f8fafc;
}

.qa-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.qa-summary {
  margin: 0 0 10px 0;
  color: #334155;
  font-size: 13px;
}

.qa-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.qa-chip {
  background: #f1f5f9;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
}

.qa-list ul {
  margin: 6px 0 0 18px;
}
.qa-list li {
  margin: 4px 0;
}
</style>
